import { useState, useEffect } from 'react';
import { Send, CheckCircle, Mail, MessageSquare, Clock } from 'lucide-react';
import { usePageTitle } from '@hooks/usePageTitle';
import ContourBG from '@components/ContourBG';

/* ─── Topographic contour background — the atlas signature ─── */
const Contact = () => {
  usePageTitle('Contact — Castle Atlas');

  const [isDark, setIsDark] = useState(() =>
    typeof window !== 'undefined' && document.documentElement.classList.contains('dark')
  );
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains('dark'))
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  const [form, setForm]       = useState({ name: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone]       = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setLoading(true);
    setError('');
    try {
      const { supabase } = await import('@services/api.js');
      await supabase.from('contact_messages').insert({
        name: form.name,
        email: form.email,
        message: form.message,
      });
      setDone(true);
    } catch {
      // Surface the failure inline rather than silently hijacking the
      // browser into the user's email client — they stay in control and
      // can retry, or use the mailto link below if they'd rather not wait.
      setError("Something went wrong sending that — please try again, or email us directly below.");
    } finally {
      setLoading(false);
    }
  };

  const c = {
    bg:         isDark ? '#1c1917' : '#ffffff',
    pageBg:     isDark ? '#0e0d0c' : '#faf9f7',
    border:     isDark ? '#2d2825' : '#e8e4df',
    head:       isDark ? '#fafaf9' : '#1c1917',
    muted:      isDark ? '#a8a29e' : '#78716c',
    mutedDim:   isDark ? '#57534e' : '#a8a29e',
    inner:      isDark ? '#151211' : '#f5f2ee',
    innerBorder:isDark ? '#2d2825' : '#e8e4df',
    dash:       isDark ? '#57534e' : '#d4cdc7',
  };

  return (
    <div id="main-content" tabIndex={-1} style={{ minHeight: '100vh', backgroundColor: c.pageBg }}>
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin   { to { transform: rotate(360deg); } }
        .cu1 { animation: fadeUp 0.35s ease-out both; }
        .cu2 { animation: fadeUp 0.35s ease-out 0.07s both; }
        .cu3 { animation: fadeUp 0.35s ease-out 0.14s both; }
        .contact-input {
          width: 100%; box-sizing: border-box;
          padding: 11px 14px;
          border-radius: 12px;
          border: 1.5px solid ${c.border};
          background: ${c.bg};
          color: ${c.head};
          font-size: 14px;
          outline: none;
          transition: border-color 0.2s ease;
          font-family: inherit;
        }
        .contact-input:focus { border-color: #ea580c; }
        .contact-input::placeholder { color: ${c.mutedDim}; }
        @media(prefers-reduced-motion:reduce){ .cu1,.cu2,.cu3 { animation:none!important; } }
      `}</style>

      {/* ── Header — cartographic ── */}
      <div style={{ position:'relative', overflow:'hidden', borderBottom:`1px solid ${c.border}` }}>
        <ContourBG isDark={isDark}/>
        <div className="cu1" style={{ maxWidth: 680, margin: '0 auto', padding: '72px 24px 48px', position:'relative', zIndex:2 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <span style={{ fontFamily:'var(--font-mono)', fontSize: 11, fontWeight: 600, color: '#ea580c', letterSpacing: '0.14em' }}>DISPATCH</span>
            <span style={{ width: 22, height: 1, background: c.dash, display: 'inline-block' }}/>
            <span style={{ fontFamily:'var(--font-mono)', fontSize: 11, fontWeight: 600, color: c.muted, letterSpacing: '0.14em', textTransform: 'uppercase' }}>Contact</span>
          </div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize: 'clamp(2rem,5vw,2.8rem)', fontWeight: 900, letterSpacing: '-0.03em', color: c.head, margin: '0 0 14px', lineHeight: 1.08 }}>
            Get in touch
          </h1>
          <p style={{ fontSize: 14.5, color: c.muted, lineHeight: 1.75, margin: 0, maxWidth: 520 }}>
            Have a question, feedback, or just want to say hello? We read every message.
            You can also email us directly at{' '}
            <a href="mailto:hello@castleatlas.co" style={{ color: '#ea580c', fontWeight: 600, textDecoration: 'none' }}>
              hello@castleatlas.co
            </a>
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '40px 24px 96px' }}>

        {/* Trust row */}
        <div className="cu2" style={{ display: 'flex', gap: 20, marginBottom: 32, flexWrap: 'wrap' }}>
          {[
            { icon: <Clock size={13} />, text: 'We reply within 24–48 hours' },
            { icon: <Mail size={13} />, text: 'hello@castleatlas.co' },
            { icon: <MessageSquare size={13} />, text: 'All messages read by a human' },
          ].map(({ icon, text }) => (
            <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: c.muted, fontWeight: 600 }}>
              <span style={{ color: '#ea580c', display: 'flex' }}>{icon}</span>
              {text}
            </div>
          ))}
        </div>

        {/* Form / Success */}
        <div className="cu3">
          {done ? (
            <div style={{
              background: c.bg, border: `1.5px solid ${c.border}`, borderRadius: 20, padding: 48,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, textAlign: 'center',
            }}>
              <div style={{ width: 56, height: 56, borderRadius: 16, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CheckCircle size={24} color="#22c55e" />
              </div>
              <div>
                <p style={{ fontFamily:'var(--font-display)', fontSize: 17, fontWeight: 800, color: c.head, marginBottom: 6 }}>Message sent!</p>
                <p style={{ fontSize: 13, color: c.muted, lineHeight: 1.6 }}>
                  We'll get back to you at <strong style={{ color: c.head }}>{form.email}</strong> within 24–48 hours.
                </p>
              </div>
              <button
                onClick={() => { setDone(false); setForm({ name: '', email: '', message: '' }); }}
                style={{ fontSize: 13, fontWeight: 700, color: '#ea580c', background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0' }}>
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ background: c.bg, border: `1.5px solid ${c.border}`, borderRadius: 20, padding: '28px 28px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                {/* Name + Email row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label htmlFor="contact-name" style={{ fontFamily:'var(--font-mono)', fontSize: 11, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 7, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Your name
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="Your name"
                      required
                      className="contact-input"
                      autoComplete="name"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" style={{ fontFamily:'var(--font-mono)', fontSize: 11, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 7, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      Email address
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      value={form.email}
                      onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      placeholder="you@example.com"
                      required
                      className="contact-input"
                      autoComplete="email"
                    />
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="contact-message" style={{ fontFamily:'var(--font-mono)', fontSize: 11, fontWeight: 700, color: c.muted, display: 'block', marginBottom: 7, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Message
                  </label>
                  <textarea
                    id="contact-message"
                    value={form.message}
                    onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                    placeholder="What's on your mind?"
                    required
                    rows={6}
                    className="contact-input"
                    style={{ resize: 'vertical', minHeight: 140, lineHeight: 1.6 }}
                  />
                </div>

                {/* Error */}
                {error && (
                  <p style={{ fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
                    {error}{' '}
                    <a
                      href={`mailto:hello@castleatlas.co?subject=${encodeURIComponent(`Castle Atlas message from ${form.name}`)}&body=${encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`)}`}
                      style={{ color: '#ef4444', textDecoration: 'underline' }}
                    >
                      Email us instead
                    </a>
                  </p>
                )}

                {/* Submit */}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="submit"
                    disabled={loading || !form.name || !form.email || !form.message}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      padding: '12px 24px', borderRadius: 12, border: 'none',
                      background: !form.name || !form.email || !form.message ? c.inner : '#ea580c',
                      color: !form.name || !form.email || !form.message ? c.muted : '#fff',
                      fontWeight: 700, fontSize: 13, cursor: !form.name || !form.email || !form.message ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: form.name && form.email && form.message ? '0 4px 14px rgba(234,88,12,0.3)' : 'none',
                    }}
                  >
                    {loading
                      ? <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                      : <><Send size={14} /> Send message</>
                    }
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Contact;
